import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
    CheckCircle, AlertCircle, ArrowLeft, Search,
    User, Users, Send, Upload, Clock, ChevronRight,
    Plus, RefreshCw, CreditCard, Fingerprint,
} from "lucide-react";
import $ from "jquery";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import DeviceStatus from "../NewAeps/DeviceStatus";

const parsePidXml = (xml) => {
    const $xml = $(xml);
    const get  = (sel, attr) => $xml.find(sel).attr(attr) || "0";
    return {
        dc:         get("DeviceInfo", "dc"),
        ci:         get("Skey",       "ci"),
        hmac:       $xml.find("Hmac").text() || "0",
        dpId:       get("DeviceInfo", "dpId"),
        mc:         get("DeviceInfo", "mc"),
        sessionKey: $xml.find("Skey").text() || "0",
        mi:         get("DeviceInfo", "mi"),
        rdsId:      get("DeviceInfo", "rdsId"),
        rdsVer:     get("DeviceInfo", "rdsVer"),
        pidData:    $xml.find("Data").text() || "0",
    };
};

const STEPS = [
    { label: "Lookup" },
    { label: "KYC" },
    { label: "Receiver" },
    { label: "Review" },
    { label: "Done" },
];

const STEP_IDX = {
    lookup: 0,
    create_customer: 1,
    upload_doc: 1,
    kyc_pending: 1,
    kyc_rejected: 1,
    ekyc: 1,
    select_receiver: 2,
    create_receiver: 2,
    initiate: 3,
    confirm: 4,
    result: 4,
};

const BACK_MAP = {
    create_customer: "lookup",
    upload_doc: "lookup",
    kyc_pending: "lookup",
    kyc_rejected: "lookup",
    ekyc: "lookup",
    select_receiver: "lookup",
    create_receiver: "select_receiver",
    initiate: "select_receiver",
    confirm: "initiate",
};

const EMPTY_CUSTOMER = {
    firstName: "", middleName: "", lastName: "",
    gender: "", dobAD: "", mobile: "",
    email: "", fatherName: "", employer: "",
    temporaryAddress: "", temporaryState: "", temporaryDistrict: "",
    temporaryCity: "", temporaryPINCode: "",
    nationality: "NPL",
    permanentAddress: "", permanentState: "", permanentDistrict: "",
    permanentLocalLevel: "", permanentWardNo: "",
    idType: "", idNumber: "", citizenshipNo: "",
    idIssuedDateAD: "", idExpiryDate: "", idIssuedPlace: "",
    customerType: "", sourceIncomeType: "", annualIncome: "",
};

const EMPTY_RECEIVER = {
    firstName: "", middleName: "", lastName: "",
    gender: "F", mobile: "", relationship: "",
    address: "", state: "", district: "", localLevel: "", country: "Nepal",
    paymentMode: "Account Deposit",
    bankBranchId: "", accountNumber: "",
    nepaleseIdType: "CITIZENSHIP", nepaleseIdNumber: "",
    nepaleseIdIssueDistrict: "", nepaleseIdIssueDate: "",
    nepaleseIdExpiryDate: "", nepaleseIdIssueCountry: "Nepal",
};

// ── UI helpers ────────────────────────────────────────────
const Lbl = ({ text, req }) => (
    <label className="block text-sm font-semibold text-gray-700 mb-1">
        {text}{req && <span className="text-red-500 ml-1">*</span>}
    </label>
);

const Inp = ({ className = "", ...props }) => (
    <input
        {...props}
        className={`w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
    />
);

const Sel = ({ children, className = "", ...props }) => (
    <select
        {...props}
        className={`w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${className}`}
    >
        {children}
    </select>
);

const SecHead = ({ title }) => (
    <div className="col-span-2 mt-4">
        <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-2">{title}</h3>
    </div>
);

const Row = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
        <span className="text-gray-500 text-sm">{label}</span>
        <span className="font-semibold text-gray-800 text-sm">{value}</span>
    </div>
);

// ── Main component ─────────────────────────────────────────
export default function IndoNepalTransaction() {
    const mid = useSelector((s) => s.auth.employeeDetails?.username);
    const { device, captureFingerprint, loading: rdLoading } = useRDService();

    const [screen, setScreen]   = useState("lookup");
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState("");

    const [customer, setCustomer]               = useState(null);
    const [selectedReceiver, setSelectedReceiver] = useState(null);

    // customer-create OTP
    const [otpSent, setOtpSent]                 = useState(false);
    const [createOtpProcId, setCreateOtpProcId] = useState("");
    const [createOtp, setCreateOtp]             = useState("");

    // ekyc
    const [ekycStep, setEkycStep]   = useState("idle"); // idle | initiated | polling | onboarding | verified
    const [ekycUrl, setEkycUrl]     = useState("");
    const ekycPollRef               = useRef(null);

    // transaction
    const [feeData, setFeeData]           = useState(null);
    const [txnOtpProcId, setTxnOtpProcId] = useState("");
    const [txnOtp, setTxnOtp]             = useState("");
    const [txnForm, setTxnForm]           = useState({ sendAmount: "", remittanceReason: "", sourceOfFund: "" });
    const [txnResult, setTxnResult]       = useState(null);

    // ref data
    const [refData, setRefData] = useState({
        relationships: [], paymentModes: [], idTypes: [],
        incomeSources: [], customerTypes: [], remittanceReasons: [],
        genders: [], annualIncomes: [], sourcesOfFund: [],
    });
    const [allNepalBanks, setAllNepalBanks] = useState([]);
    const [banks, setBanks]                = useState([]);
    const [bankSearch, setBankSearch]      = useState("");
    const [showBankDrop, setShowBankDrop]  = useState(false);

    // customer-create success popup
    const [showCreateSuccess, setShowCreateSuccess] = useState(false);
    const [createdMobile, setCreatedMobile]         = useState("");

    // forms
    const [searchType, setSearchType]   = useState("MOBILE");
    const [searchValue, setSearchValue] = useState("");
    const [newCustomer, setNewCustomer] = useState({ ...EMPTY_CUSTOMER });
    const [newReceiver, setNewReceiver] = useState({ ...EMPTY_RECEIVER });
    const [docFiles, setDocFiles] = useState({ frontImg: null, backImg: null, additionalImg: null });
    const [showUploadModal,   setShowUploadModal]   = useState(false);
    const [uploadCustomerId,  setUploadCustomerId]  = useState(null);

    // API hooks
    const { callApi: lookupApi }         = useApi("/indo-nepal/customer/lookup",     "POST");
    const { callApi: customerOtpApi }    = useApi("/indo-nepal/customer/otp",        "POST");
    const { callApi: customerCreateApi } = useApi("/indo-nepal/customer/create",     "POST");
    const { callApi: uploadDocApi }      = useApi("/indo-nepal/customer/upload-doc", "POST");
    const { callApi: receiverCreateApi } = useApi("/indo-nepal/receiver/create",     "POST");
    const { callApi: txnInitiateApi }    = useApi("/indo-nepal/txn/initiate",        "POST");
    const { callApi: txnSendApi }        = useApi("/indo-nepal/txn/send",            "POST");
    const { callApi: staticDataApi }     = useApi("/indo-nepal/data/static",              "GET");
    const { callApi: banksApi }          = useApi("/indo-nepal/data/banks",               "GET");
    const { callApi: geoApi }            = useApi("/indo-nepal/data/state-district",      "GET");
    const { callApi: ekycInitiateApi }   = useApi("/indo-nepal/customer/ekyc/initiate",   "POST");
    const { callApi: ekycPollApi }       = useApi("/indo-nepal/customer/ekyc/poll",       "POST");
    const { callApi: ekycOnboardApi }    = useApi("/indo-nepal/customer/ekyc/onboard",    "POST");
    const { callApi: ekycEnrollApi }     = useApi("/indo-nepal/customer/ekyc/enroll",     "POST");

    const [indiaGeoData, setIndiaGeoData] = useState([]);
    const [nepalGeoData, setNepalGeoData] = useState([]);

    // load dropdowns on mount
    useEffect(() => {
        const load = async () => {
            try {
                const types = ["Relationship", "PaymentMode", "IDType", "SOURCEINCOMETYPE", "CUSTOMERTYPE", "RemittanceReason", "Gender", "AnnualIncome", "SourceOfFund"];
                const results = await Promise.allSettled(types.map(t => staticDataApi(null, `type=${t}`)));
                const normalize = (v) =>
                    Array.isArray(v?.data?.dataList) ? v.data.dataList :
                    Array.isArray(v?.data?.data) ? v.data.data :
                    Array.isArray(v?.data) ? v.data :
                    Array.isArray(v) ? v : [];
                const [rel, pm, it, is_, ct, rr, gd, ai, sof] = results.map(r =>
                    r.status === "fulfilled" ? normalize(r.value) : []
                );
                setRefData({ relationships: rel, paymentModes: pm, idTypes: it, incomeSources: is_, customerTypes: ct, remittanceReasons: rr, genders: gd, annualIncomes: ai, sourcesOfFund: sof });
            } catch { /* silent */ }

            try {
                const toArr = (v) =>
                    Array.isArray(v) ? v :
                    Array.isArray(v?.data?.data) ? v.data.data :
                    Array.isArray(v?.data) ? v.data :
                    Array.isArray(v?.stateDistricts) ? v.stateDistricts :
                    [];
                const [indiaRes, nepalRes] = await Promise.allSettled([
                    geoApi(null, "country=INDIA"),
                    geoApi(null, "country=NEPAL"),
                ]);
                setIndiaGeoData(indiaRes.status === "fulfilled" ? toArr(indiaRes.value) : []);
                setNepalGeoData(nepalRes.status === "fulfilled" ? toArr(nepalRes.value) : []);
            } catch { /* silent */ }

            try {
                const res = await banksApi(null, "country=Nepal");
                const list = res?.data?.bankBranches || res?.data || [];
                setAllNepalBanks(list);
                setBanks(list);
            } catch { /* silent */ }
        };
        load();
    }, []);

    // client-side filter only — API called once on mount
    useEffect(() => {
        if (!bankSearch.trim()) { setBanks(allNepalBanks); return; }
        const lower = bankSearch.toLowerCase();
        setBanks(allNepalBanks.filter(b =>
            (b.bankName   || "").toLowerCase().includes(lower) ||
            (b.branchName || "").toLowerCase().includes(lower)
        ));
    }, [bankSearch, allNepalBanks]);

    const goBack = () => {
        const prev = BACK_MAP[screen];
        if (prev) { setError(""); setScreen(prev); }
    };

    const optVal = (item) => item?.code || item?.value || item?.id || item;
    const optLbl = (item) => item?.name || item?.label || item?.value || item;

    const idTypeRequiresDoc = (idType) =>
        ["4", "12", "passport", "citizenship"].includes(String(idType).toLowerCase());

    // ── Handlers ─────────────────────────────────────────

    const handleLookup = async () => {
        if (!searchValue.trim()) { setError("Enter a mobile number or ID."); return; }
        try {
            setLoading(true); setError("");
            const payload = searchType === "MOBILE"
                ? { merchantId: mid, mobile: searchValue.trim(), searchType }
                : { merchantId: mid, idNumber: searchValue.trim(), searchType };
            const res = await lookupApi(payload);
            const d = res?.data;
            if (!d?.found) {
                setNewCustomer({ ...EMPTY_CUSTOMER, mobile: searchType === "MOBILE" ? searchValue.trim() : "" });
                setOtpSent(false); setCreateOtp(""); setCreateOtpProcId("");
                setCustomer(null);
                setScreen("create_customer");
            } else {
                setCustomer(d);
                const next = d.kycNextStep;
                if (next === "ready")             setScreen("select_receiver");
                else if (next === "upload_doc")  setScreen("upload_doc");
                else if (next === "kyc_pending") setScreen("kyc_pending");
                else if (next === "kyc_rejected") setScreen("kyc_rejected");
                else if (next === "ekyc_required") { setEkycStep("idle"); setEkycUrl(""); setScreen("ekyc"); }
                else setScreen("select_receiver");
            }
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Lookup failed");
        } finally { setLoading(false); }
    };

    const handleSendOtp = async () => {
        if (!newCustomer.firstName || !newCustomer.lastName || !newCustomer.mobile || !newCustomer.idType) {
            setError("Fill First Name, Last Name, Mobile, and ID Type before sending OTP."); return;
        }
        try {
            setLoading(true); setError("");
            const res = await customerOtpApi({
                merchantId: mid,
                operation: "CreateCustomer",
                mobile: newCustomer.mobile,
                customerFullName: `${newCustomer.firstName} ${newCustomer.middleName} ${newCustomer.lastName}`.replace(/\s+/g, " ").trim(),
                idType: newCustomer.idType,
            });
            setCreateOtpProcId(res?.data?.otpProcessId || "");
            setOtpSent(true);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Failed to send OTP");
        } finally { setLoading(false); }
    };

    const handleCreateCustomer = async () => {
        if (!createOtp) { setError("Enter the OTP."); return; }
        try {
            setLoading(true); setError("");
            const resolvedIdNumber = String(newCustomer.idType) === "12"
                ? newCustomer.citizenshipNo
                : newCustomer.idNumber;
            const res = await customerCreateApi({
                merchantId: mid,
                ...newCustomer,
                idNumber: resolvedIdNumber,
                otp: createOtp,
                otpProcessId: createOtpProcId,
            });
            if (res?.data?.code !== "000") {
                setError(res?.data?.message || res?.message || "Customer creation failed");
                return;
            }
            setOtpSent(false); setCreateOtp(""); setCreateOtpProcId("");
            setCreatedMobile(newCustomer.mobile);
            setShowCreateSuccess(true);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Failed to create customer");
        } finally { setLoading(false); }
    };

    const handleCreateSuccessOk = () => {
        setShowCreateSuccess(false);
        setSearchType("MOBILE");
        setSearchValue(createdMobile);
        setNewCustomer({ ...EMPTY_CUSTOMER });
        setScreen("lookup");
    };

    const handleUploadAndProceed = async () => {
        if (!docFiles.frontImg) { setError("Please upload the front page document."); return; }
        try {
            setLoading(true); setError("");
            const fd = new FormData();
            fd.append("merchantId", mid);
            fd.append("customerId", uploadCustomerId);
            fd.append("idType", newCustomer.idType);
            fd.append("frontImg", docFiles.frontImg);
            if (docFiles.backImg)       fd.append("backImg", docFiles.backImg);
            if (docFiles.additionalImg) fd.append("additionalImg", docFiles.additionalImg);
            const uploadRes = await uploadDocApi(fd);
            if (uploadRes?.data?.code !== "000") {
                setError(uploadRes?.data?.message || uploadRes?.message || "Document upload failed");
                return;
            }
            setShowUploadModal(false);
            setDocFiles({ frontImg: null, backImg: null, additionalImg: null });
            setScreen("select_receiver");
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Document upload failed");
        } finally { setLoading(false); }
    };

    // ── eKYC handlers ──────────────────────────────────────
    const stopEkycPoll = () => { if (ekycPollRef.current) { clearInterval(ekycPollRef.current); ekycPollRef.current = null; } };

    const handleEkycInitiate = async () => {
        try {
            setLoading(true); setError("");
            const res = await ekycInitiateApi({ merchantId: mid, customerId: customer?.customerId });
            const url = res?.data?.eKycUrl;
            if (!url) throw new Error("eKYC URL not received");
            setEkycUrl(url);
            window.open(url, "_blank");
            setEkycStep("polling");
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "eKYC initiation failed");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (ekycStep !== "polling") { stopEkycPoll(); return; }
        ekycPollRef.current = setInterval(async () => {
            try {
                const res = await ekycPollApi({ merchantId: mid, customerId: customer?.customerId });
                if (Number(res?.data?.StatusCode) === 1) {
                    stopEkycPoll();
                    if (customer?.onboardingStatus !== "Success") setEkycStep("onboarding");
                    else setEkycStep("verified");
                }
            } catch { /* keep polling */ }
        }, 8000);
        return stopEkycPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ekycStep]);

    const handleEkycOnboard = async () => {
        try {
            setLoading(true); setError("");
            const res = await ekycOnboardApi({ merchantId: mid, customerId: customer?.customerId });
            if (Number(res?.data?.StatusCode) === 1) {
                setEkycStep("verified");
            } else {
                // BIO KYC pending — need fingerprint enrollment
                setEkycStep("bio_kyc");
            }
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Onboarding failed");
        } finally { setLoading(false); }
    };

    const handleBioEnroll = async () => {
        try {
            setLoading(true); setError("");
            setEkycStep("bio_capturing");
            const wadhValue = "TF/lfPuh1n4ZY1xizYpqikIBm+gv65r51MFNek4uwNw=";
            const pidXml = await captureFingerprint(wadhValue);
            if (!pidXml || !pidXml.includes('errCode="0"')) {
                setEkycStep("bio_kyc");
                setError("Fingerprint capture failed. Please try again.");
                return;
            }
            const p = parsePidXml(pidXml);
            const enrollRes = await ekycEnrollApi({
                merchantId:                     mid,
                customerId:                     customer?.customerId,
                encryptedPid:                   p.pidData,
                encryptedHmac:                  p.hmac,
                sessionKeyValue:                p.sessionKey,
                certificateIdentifier:          Number(p.ci) || 0,
                registeredDeviceServiceId:      p.rdsId,
                registeredDeviceServiceVersion: p.rdsVer,
                registeredDeviceProviderId:     p.dpId,
                registeredDeviceCode:           p.dc,
                registeredDeviceModelId:        p.mi,
                registeredDevicePublicKey:      p.mc,
            });
            if (Number(enrollRes?.data?.StatusCode) === 1) {
                setEkycStep("verified");
            } else {
                throw new Error(enrollRes?.data?.ResponseMessage || "Biometric enrollment failed");
            }
        } catch (err) {
            setEkycStep("bio_kyc");
            setError(err?.response?.data?.message || err?.message || "Enrollment failed");
        } finally { setLoading(false); }
    };

    const handleEkycDone = async () => {
        try {
            setLoading(true); setError("");
            const payload = { merchantId: mid, mobile: customer?.mobile, searchType: "MOBILE" };
            const res = await lookupApi(payload);
            const d = res?.data;
            if (d?.kycNextStep === "ready") { setCustomer(d); setScreen("select_receiver"); }
            else setError("eKYC not yet fully verified. Please wait and try again.");
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Lookup failed");
        } finally { setLoading(false); }
    };

    const handleFileChange = (field, file) => {
        if (!file) return;
        setDocFiles(prev => ({ ...prev, [field]: file }));
    };

    const handleUploadDoc = async () => {
        if (!docFiles.frontImg) { setError("Upload the front image."); return; }
        try {
            setLoading(true); setError("");
            const fd = new FormData();
            fd.append("merchantId", mid);
            fd.append("customerId", customer?.customerId);
            fd.append("idType", customer?.idType);
            fd.append("frontImg", docFiles.frontImg);
            if (docFiles.backImg)       fd.append("backImg", docFiles.backImg);
            if (docFiles.additionalImg) fd.append("additionalImg", docFiles.additionalImg);
            await uploadDocApi(fd);
            setScreen("kyc_pending");
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Upload failed");
        } finally { setLoading(false); }
    };

    const handleSelectReceiver = (rec) => {
        setSelectedReceiver(rec);
        setFeeData(null); setTxnOtp(""); setTxnOtpProcId("");
        setTxnForm({ sendAmount: "", remittanceReason: "", sourceOfFund: "" });
        setScreen("initiate");
    };

    const handleCreateReceiver = async () => {
        if (!newReceiver.firstName || !newReceiver.lastName || !newReceiver.mobile || !newReceiver.relationship) {
            setError("Fill receiver name, mobile, and relationship."); return;
        }
        try {
            setLoading(true); setError("");
            const res = await receiverCreateApi({
                merchantId: mid,
                customerId: customer?.customerId,
                ...newReceiver,
            });
            if (res?.success !== true || res?.data?.code !== "000" || !res?.data?.receiverId) {
                setError(res?.data?.message || res?.message || "Failed to create receiver. Please try again.");
                return;
            }
            handleSelectReceiver({
                receiverId: res?.data?.receiverId,
                name: `${newReceiver.firstName} ${newReceiver.lastName}`.trim(),
                paymentMode: newReceiver.paymentMode,
                acNumber: newReceiver.accountNumber,
            });
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Failed to create receiver");
        } finally { setLoading(false); }
    };

    const handleInitiate = async () => {
        const amt = parseFloat(txnForm.sendAmount);
        if (!amt || amt <= 0) { setError("Enter a valid transfer amount."); return; }
        try {
            setLoading(true); setError("");
            const res = await txnInitiateApi({
                merchantId: mid,
                customerId: customer?.customerId,
                receiverId: selectedReceiver?.receiverId,
                paymentMode: selectedReceiver?.paymentMode || "Account Deposit",
                transferAmount: amt,
                mobile: customer?.mobile,
                customerFullName: customer?.name,
                idType: customer?.idType,
            });
            const d = res?.data;
            setFeeData(d);
            setTxnOtpProcId(d?.otpProcessId || "");
            setTxnOtp("");
            setScreen("confirm");
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Failed to get fee quote");
        } finally { setLoading(false); }
    };

    const handleSend = async () => {
        if (!txnOtp) { setError("Enter the OTP from the customer."); return; }
        if (!txnForm.remittanceReason) { setError("Select a remittance reason."); return; }
        if (!txnForm.sourceOfFund)     { setError("Select a source of fund."); return; }
        try {
            setLoading(true); setError("");
            const res = await txnSendApi({
                merchantId: mid,
                customerId: customer?.customerId,
                senderName: customer?.name,
                senderMobile: customer?.mobile,
                senderIDType: customer?.idType,
                receiverId: selectedReceiver?.receiverId,
                receiverName: selectedReceiver?.name,
                paymentMode: selectedReceiver?.paymentMode || "Account Deposit",
                sendAmount: feeData?.transferAmount,
                accountNumber: selectedReceiver?.acNumber,
                remittanceReason: txnForm.remittanceReason,
                sourceOfFund: txnForm.sourceOfFund,
                otp: txnOtp,
                otpProcessId: txnOtpProcId,
            });
            setTxnResult(res?.data);
            setScreen("result");
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Transaction failed");
        } finally { setLoading(false); }
    };

    const handleReset = () => {
        setScreen("lookup"); setSearchValue(""); setSearchType("MOBILE");
        setCustomer(null); setSelectedReceiver(null);
        setFeeData(null); setTxnResult(null);
        setTxnOtp(""); setTxnOtpProcId("");
        setTxnForm({ sendAmount: "", remittanceReason: "", sourceOfFund: "" });
        setError("");
    };

    const stepIdx  = STEP_IDX[screen] ?? 0;
    const canGoBack = !!BACK_MAP[screen];

    // ── Render ────────────────────────────────────────────
    return (
        <>
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="w-full">

                {/* Title */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Indo Nepal Remittance</h1>
                    <p className="text-gray-500 mt-1 text-lg">Send money to Nepal for your customers</p>
                </div>

                {/* Progress bar */}
                <div className="mb-10 px-4">
                    <div className="relative flex items-start justify-between">
                        <div className="absolute top-5 left-5 right-5 h-1 bg-gray-200 z-0" />
                        <div
                            className="absolute top-5 left-5 h-1 bg-blue-600 z-0 transition-all duration-500"
                            style={{ width: stepIdx === 0 ? "0%" : `${(stepIdx / (STEPS.length - 1)) * 100}%` }}
                        />
                        {STEPS.map((s, i) => (
                            <div key={i} className="flex flex-col items-center z-10">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all
                                    ${i < stepIdx  ? "bg-blue-600 border-blue-600 text-white" : ""}
                                    ${i === stepIdx ? "bg-blue-600 border-blue-600 text-white ring-4 ring-blue-100" : ""}
                                    ${i > stepIdx  ? "bg-white border-gray-300 text-gray-400" : ""}
                                `}>
                                    {i < stepIdx ? <CheckCircle size={18} /> : i + 1}
                                </div>
                                <span className={`mt-2 text-sm font-semibold ${i <= stepIdx ? "text-blue-600" : "text-gray-400"}`}>
                                    {s.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Card */}
                <div className="w-full bg-white rounded-2xl shadow-sm border p-8">

                    {canGoBack && (
                        <button onClick={goBack} className="flex items-center gap-2 text-gray-500 hover:text-blue-600 text-sm font-medium mb-6 transition-colors">
                            <ArrowLeft size={18} /> Back
                        </button>
                    )}

                    {error && (
                        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
                            <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}

                    {/* ═══ LOOKUP ═══ */}
                    {screen === "lookup" && (
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                                    <Search size={22} className="text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Find Customer</h2>
                                    <p className="text-gray-500 text-sm">Search by mobile or ID number</p>
                                </div>
                            </div>

                            <div className="flex gap-2 mb-4">
                                {[["MOBILE", "By Mobile"], ["ID", "By ID Number"]].map(([val, lbl]) => (
                                    <button key={val}
                                        onClick={() => { setSearchType(val); setSearchValue(""); setError(""); }}
                                        className={`px-5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                                            searchType === val
                                                ? "bg-blue-600 text-white border-blue-600"
                                                : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                                        }`}
                                    >{lbl}</button>
                                ))}
                            </div>

                            <div className="flex gap-3">
                                <input
                                    type="text"
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleLookup()}
                                    placeholder={searchType === "MOBILE" ? "Enter 10-digit mobile number" : "Enter ID / Passport number"}
                                    className="flex-1 border border-gray-300 rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    onClick={handleLookup}
                                    disabled={loading}
                                    className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold disabled:opacity-50 transition-colors flex items-center gap-2"
                                >
                                    <Search size={18} />
                                    {loading ? "Searching..." : "Search"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ═══ CREATE CUSTOMER — Phase 1: OTP trigger ═══ */}
                    {screen === "create_customer" && !otpSent && (
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                                    <User size={22} className="text-green-600" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Register New Customer</h2>
                                    <p className="text-gray-500 text-sm">Customer not found — enter basic details to send OTP</p>
                                </div>
                            </div>

                            {/* Step indicator */}
                            <div className="flex items-center gap-2 mb-6 mt-4">
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">1</div>
                                <span className="text-sm font-semibold text-blue-700">Basic Details</span>
                                <div className="flex-1 h-px bg-gray-200 mx-2" />
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-400 text-xs font-bold">2</div>
                                <span className="text-sm text-gray-400">Complete Profile + OTP</span>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div><Lbl text="First Name" req /><Inp value={newCustomer.firstName} onChange={(e) => setNewCustomer(p => ({...p, firstName: e.target.value}))} placeholder="First name" /></div>
                                <div><Lbl text="Last Name" req /><Inp value={newCustomer.lastName} onChange={(e) => setNewCustomer(p => ({...p, lastName: e.target.value}))} placeholder="Last name" /></div>
                                <div>
                                    <Lbl text="Mobile" req />
                                    <Inp
                                        value={newCustomer.mobile}
                                        onChange={(e) => setNewCustomer(p => ({...p, mobile: e.target.value.replace(/\D/g, "").slice(0, 10)}))}
                                        placeholder="10-digit mobile"
                                    />
                                </div>
                                <div>
                                    <Lbl text="ID Type" req />
                                    <Sel value={newCustomer.idType} onChange={(e) => setNewCustomer(p => ({...p, idType: e.target.value}))}>
                                        <option value="">Select ID type</option>
                                        {refData.idTypes.length > 0
                                            ? refData.idTypes.map((t, i) => <option key={i} value={optVal(t)}>{optLbl(t)}</option>)
                                            : <><option value="4">Passport</option><option value="12">Citizenship</option><option value="77">Aadhaar</option></>
                                        }
                                    </Sel>
                                </div>
                            </div>

                            <button
                                onClick={handleSendOtp}
                                disabled={loading}
                                className="w-full mt-8 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold text-base disabled:opacity-50 transition-colors flex items-center justify-center gap-3 shadow-md shadow-blue-100"
                            >
                                {loading ? (
                                    <><RefreshCw size={20} className="animate-spin" /> Sending OTP...</>
                                ) : (
                                    <><Send size={20} /> Send OTP to Customer</>
                                )}
                            </button>
                        </div>
                    )}

                    {/* ═══ CREATE CUSTOMER — Phase 2: Full details + OTP ═══ */}
                    {screen === "create_customer" && otpSent && (
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                                    <User size={22} className="text-green-600" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Complete Registration</h2>
                                    <p className="text-gray-500 text-sm">OTP sent — fill remaining details and confirm</p>
                                </div>
                            </div>

                            {/* Step indicator */}
                            <div className="flex items-center gap-2 mb-6 mt-4">
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold">
                                    <CheckCircle size={14} />
                                </div>
                                <span className="text-sm font-semibold text-green-700">Basic Details</span>
                                <div className="flex-1 h-px bg-blue-400 mx-2" />
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">2</div>
                                <span className="text-sm font-semibold text-blue-700">Complete Profile + OTP</span>
                            </div>

                            {/* OTP box — prominent, at the top */}
                            <div className="mb-6 p-5 bg-green-50 border border-green-200 rounded-2xl">
                                <p className="text-sm text-green-700 font-semibold mb-3 flex items-center gap-2">
                                    <CheckCircle size={16} /> OTP sent to <span className="font-bold">{newCustomer.mobile}</span>
                                </p>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Enter OTP <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text" maxLength={6} value={createOtp}
                                    onChange={(e) => setCreateOtp(e.target.value.replace(/\D/g, ""))}
                                    placeholder="• • • • • •"
                                    className="w-full border-2 border-green-300 rounded-xl px-4 py-4 text-3xl tracking-[0.5em] text-center font-mono focus:outline-none focus:border-green-500 bg-white"
                                />
                                <button
                                    onClick={() => { setOtpSent(false); setCreateOtp(""); }}
                                    className="mt-2 text-sm text-blue-600 hover:underline font-medium"
                                >
                                    Resend OTP
                                </button>
                            </div>

                            {/* Remaining customer fields */}
                            <div className="grid grid-cols-2 gap-4">
                                <SecHead title="Personal Details" />
                                <div>
                                    <Lbl text="Gender" req />
                                    <Sel value={newCustomer.gender} onChange={(e) => setNewCustomer(p => ({...p, gender: e.target.value}))}>
                                        <option value="">Select gender</option>
                                        {refData.genders.length > 0
                                            ? refData.genders.map((g, i) => <option key={i} value={optVal(g)}>{optLbl(g)}</option>)
                                            : <><option value="M">Male</option><option value="F">Female</option><option value="O">Other</option></>
                                        }
                                    </Sel>
                                </div>
                                <div><Lbl text="Date of Birth" req /><Inp type="date" value={newCustomer.dobAD} onChange={(e) => setNewCustomer(p => ({...p, dobAD: e.target.value}))} /></div>
                                <div><Lbl text="Email" /><Inp type="email" value={newCustomer.email} onChange={(e) => setNewCustomer(p => ({...p, email: e.target.value}))} placeholder="email@example.com" /></div>
                                <div><Lbl text="Father's Name" /><Inp value={newCustomer.fatherName} onChange={(e) => setNewCustomer(p => ({...p, fatherName: e.target.value}))} placeholder="Father's full name" /></div>
                                <div className="col-span-2">
                                    <Lbl text="Employer / Occupation" />
                                    <Sel value={newCustomer.employer} onChange={(e) => setNewCustomer(p => ({...p, employer: e.target.value}))}>
                                        <option value="">Select occupation</option>
                                        {refData.customerTypes.length > 0
                                            ? refData.customerTypes.map((t, i) => <option key={i} value={optVal(t)}>{optLbl(t)}</option>)
                                            : <><option value="16">Salaried</option><option value="15">SelfEmployed</option><option value="14">Farmer</option><option value="17">Housewife</option></>
                                        }
                                    </Sel>
                                </div>

                                <SecHead title="ID / KYC Details" />
                                {/* idType "77" (Aadhaar): show only ID Number */}
                                {/* idType "4" or "12": hide ID Number, show remaining */}
                                {String(newCustomer.idType) !== "4" && String(newCustomer.idType) !== "12" && (
                                    <div className="col-span-2">
                                        <Lbl text={String(newCustomer.idType) === "77" ? "Aadhaar Number" : "ID Number"} req />
                                        <Inp value={newCustomer.idNumber} onChange={(e) => setNewCustomer(p => ({...p, idNumber: e.target.value}))} placeholder={String(newCustomer.idType) === "77" ? "12-digit Aadhaar number" : "Passport / Citizenship no."} />
                                    </div>
                                )}
                                {String(newCustomer.idType) !== "77" && (
                                    <>
                                        <div><Lbl text="Citizenship No" /><Inp value={newCustomer.citizenshipNo} onChange={(e) => setNewCustomer(p => ({...p, citizenshipNo: e.target.value}))} placeholder="Citizenship number" /></div>
                                        <div><Lbl text="ID Issued Place" req /><Inp value={newCustomer.idIssuedPlace} onChange={(e) => setNewCustomer(p => ({...p, idIssuedPlace: e.target.value}))} placeholder="City / District of issue" /></div>
                                        <div><Lbl text="ID Issue Date" req /><Inp type="date" value={newCustomer.idIssuedDateAD} onChange={(e) => setNewCustomer(p => ({...p, idIssuedDateAD: e.target.value}))} /></div>
                                        <div><Lbl text="ID Expiry Date" /><Inp type="date" value={newCustomer.idExpiryDate} onChange={(e) => setNewCustomer(p => ({...p, idExpiryDate: e.target.value}))} /></div>
                                    </>
                                )}

                                <SecHead title="India Address (Temporary)" />
                                <div className="col-span-2"><Lbl text="Address" req /><Inp value={newCustomer.temporaryAddress} onChange={(e) => setNewCustomer(p => ({...p, temporaryAddress: e.target.value}))} placeholder="Street / House no." /></div>
                                <div>
                                    <Lbl text="State" req />
                                    <Sel value={newCustomer.temporaryState} onChange={(e) => setNewCustomer(p => ({...p, temporaryState: e.target.value, temporaryDistrict: ""}))}>
                                        <option value="">Select state</option>
                                        {[...new Map((indiaGeoData || []).map(d => [d.stateCode, d.state])).entries()]
                                            .sort((a, b) => a[1].localeCompare(b[1]))
                                            .map(([code, name], i) => (
                                                <option key={i} value={code}>{name}</option>
                                            ))
                                        }
                                    </Sel>
                                </div>
                                <div>
                                    <Lbl text="District" req />
                                    <Sel value={newCustomer.temporaryDistrict} onChange={(e) => setNewCustomer(p => ({...p, temporaryDistrict: e.target.value}))} disabled={!newCustomer.temporaryState}>
                                        <option value="">Select district</option>
                                        {(indiaGeoData || []).filter(d => d.stateCode === newCustomer.temporaryState).map((d, i) => (
                                            <option key={i} value={d.district}>{d.district}</option>
                                        ))}
                                    </Sel>
                                </div>
                                <div><Lbl text="City" req /><Inp value={newCustomer.temporaryCity} onChange={(e) => setNewCustomer(p => ({...p, temporaryCity: e.target.value}))} placeholder="City" /></div>
                                <div><Lbl text="PIN Code" req /><Inp value={newCustomer.temporaryPINCode} onChange={(e) => setNewCustomer(p => ({...p, temporaryPINCode: e.target.value.replace(/\D/g, "").slice(0, 6)}))} placeholder="6-digit PIN" /></div>

                                <SecHead title="Nepal Address (Permanent)" />
                                <div className="col-span-2"><Lbl text="Address" req /><Inp value={newCustomer.permanentAddress} onChange={(e) => setNewCustomer(p => ({...p, permanentAddress: e.target.value}))} placeholder="Address in Nepal" /></div>
                                <div>
                                    <Lbl text="State / Province" req />
                                    <Sel value={newCustomer.permanentState} onChange={(e) => setNewCustomer(p => ({...p, permanentState: e.target.value, permanentDistrict: ""}))}>
                                        <option value="">Select province</option>
                                        {[...new Map((nepalGeoData || []).map(d => [d.stateCode, d.state])).entries()]
                                            .sort((a, b) => a[1].localeCompare(b[1]))
                                            .map(([code, name], i) => (
                                                <option key={i} value={code}>{name}</option>
                                            ))
                                        }
                                    </Sel>
                                </div>
                                <div>
                                    <Lbl text="District" req />
                                    <Sel value={newCustomer.permanentDistrict} onChange={(e) => setNewCustomer(p => ({...p, permanentDistrict: e.target.value, permanentLocalLevel: ""}))} disabled={!newCustomer.permanentState}>
                                        <option value="">Select district</option>
                                        {(nepalGeoData || []).filter(d => d.stateCode === newCustomer.permanentState).map((d, i) => (
                                            <option key={i} value={d.district}>{d.district}</option>
                                        ))}
                                    </Sel>
                                </div>
                                <div>
                                    <Lbl text="Local Level" req />
                                    <Sel value={newCustomer.permanentLocalLevel} onChange={(e) => setNewCustomer(p => ({...p, permanentLocalLevel: e.target.value}))} disabled={!newCustomer.permanentDistrict}>
                                        <option value="">Select local level</option>
                                        {(nepalGeoData || [])
                                            .filter(d => d.stateCode === newCustomer.permanentState && d.district === newCustomer.permanentDistrict && d.localLevel)
                                            .map((d, i) => (
                                                <option key={i} value={d.localLevel}>{d.localLevel}</option>
                                            ))
                                        }
                                    </Sel>
                                </div>
                                <div><Lbl text="Ward No" req /><Inp value={newCustomer.permanentWardNo} onChange={(e) => setNewCustomer(p => ({...p, permanentWardNo: e.target.value}))} placeholder="Ward number" /></div>

                                <SecHead title="Financial" />
                                <div>
                                    <Lbl text="Customer Type" req />
                                    <Sel value={newCustomer.customerType} onChange={(e) => setNewCustomer(p => ({...p, customerType: e.target.value}))}>
                                        <option value="">Select type</option>
                                        {refData.customerTypes.length > 0
                                            ? refData.customerTypes.map((t, i) => <option key={i} value={optVal(t)}>{optLbl(t)}</option>)
                                            : <><option value="INDIVIDUAL">Individual</option><option value="COMPANY">Company</option></>
                                        }
                                    </Sel>
                                </div>
                                <div>
                                    <Lbl text="Source of Income" req />
                                    <Sel value={newCustomer.sourceIncomeType} onChange={(e) => setNewCustomer(p => ({...p, sourceIncomeType: e.target.value}))}>
                                        <option value="">Select source</option>
                                        {refData.incomeSources.length > 0
                                            ? refData.incomeSources.map((t, i) => <option key={i} value={optVal(t)}>{optLbl(t)}</option>)
                                            : <><option value="EMPLOYMENT">Employment</option><option value="BUSINESS">Business</option><option value="SAVINGS">Savings</option></>
                                        }
                                    </Sel>
                                </div>
                                <div className="col-span-2">
                                    <Lbl text="Annual Income" />
                                    <Sel value={newCustomer.annualIncome} onChange={(e) => setNewCustomer(p => ({...p, annualIncome: e.target.value}))}>
                                        <option value="">Select range</option>
                                        {refData.annualIncomes.length > 0
                                            ? refData.annualIncomes.map((a, i) => <option key={i} value={optVal(a)}>{optLbl(a)}</option>)
                                            : <><option value="100000">Up to ₹1 Lakh</option><option value="300000">₹1–3 Lakh</option><option value="500000">₹3–5 Lakh</option><option value="1000000">Above ₹5 Lakh</option></>
                                        }
                                    </Sel>
                                </div>
                            </div>

                            <button
                                onClick={handleCreateCustomer}
                                disabled={loading || createOtp.length < 4}
                                className="w-full mt-8 bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold text-base disabled:opacity-50 transition-colors flex items-center justify-center gap-3 shadow-md shadow-green-100"
                            >
                                {loading ? (
                                    <><RefreshCw size={20} className="animate-spin" /> Creating Customer...</>
                                ) : (
                                    <>Create Customer &amp; Continue <ChevronRight size={20} /></>
                                )}
                            </button>
                        </div>
                    )}

                    {/* ═══ UPLOAD DOC ═══ */}
                    {screen === "upload_doc" && (
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center">
                                    <Upload size={22} className="text-orange-600" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Upload KYC Documents</h2>
                                    <p className="text-gray-500 text-sm">Customer: {customer?.name}</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {[
                                    ["frontImg",       "Front Page",           true],
                                    ["backImg",        "Back Page",            false],
                                    ["additionalImg",  "Selfie with Document", false],
                                ].map(([field, label, required]) => (
                                    <div key={field} className="border-2 border-dashed border-gray-300 rounded-xl p-5">
                                        <p className="text-sm font-semibold text-gray-700 mb-3">{label}{required && <span className="text-red-500 ml-1">*</span>}</p>
                                        <input type="file" accept="image/*"
                                            onChange={(e) => handleFileChange(field, e.target.files?.[0])}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                        {docFiles[field] && (
                                            <p className="text-xs text-green-600 mt-2 flex items-center gap-1"><CheckCircle size={12} /> Image loaded</p>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <button onClick={handleUploadDoc} disabled={loading || !docFiles.frontImg}
                                className="w-full mt-6 bg-orange-600 hover:bg-orange-700 text-white py-4 rounded-xl font-semibold disabled:opacity-50 transition-colors">
                                {loading ? "Uploading..." : "Upload Documents"}
                            </button>
                        </div>
                    )}

                    {/* ═══ KYC PENDING ═══ */}
                    {screen === "kyc_pending" && (
                        <div className="text-center py-8">
                            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Clock size={40} className="text-amber-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">KYC Under Review</h2>
                            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                                Documents submitted. Prabhu's team will verify within 24–48 hours.
                            </p>
                            <div className="p-5 bg-amber-50 rounded-xl border border-amber-200 text-amber-700 text-sm max-w-sm mx-auto">
                                <p className="font-semibold">{customer?.name}</p>
                                <p className="text-xs mt-1">Customer ID: {customer?.customerId}</p>
                            </div>
                            <button onClick={() => { setScreen("lookup"); setSearchValue(""); setError(""); }}
                                className="mt-8 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors">
                                Search Another Customer
                            </button>
                        </div>
                    )}

                    {/* ═══ KYC REJECTED ═══ */}
                    {screen === "kyc_rejected" && (
                        <div className="text-center py-8">
                            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <AlertCircle size={40} className="text-red-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">KYC Documents Rejected</h2>
                            {customer?.approveComment && (
                                <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm max-w-sm mx-auto mb-6">
                                    <p className="font-semibold mb-1">Rejection Reason:</p>
                                    <p>{customer.approveComment}</p>
                                </div>
                            )}
                            <button onClick={() => setScreen("upload_doc")}
                                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors">
                                Re-upload Documents
                            </button>
                        </div>
                    )}

                    {/* ═══ eKYC ═══ */}
                    {screen === "ekyc" && (
                        <div className="text-center py-8">
                            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle size={40} className="text-blue-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Aadhaar eKYC Required</h2>
                            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                                Complete Aadhaar biometric verification to activate this customer.
                            </p>

                            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-sm max-w-sm mx-auto mb-8 text-left">
                                <p className="font-semibold text-blue-800">{customer?.name}</p>
                                <p className="text-blue-600 text-xs mt-1">ID: {customer?.customerId} &nbsp;|&nbsp; eKYC: {customer?.ekycStatus}</p>
                            </div>

                            {/* Step A — Initiate */}
                            {ekycStep === "idle" && (
                                <button onClick={handleEkycInitiate} disabled={loading}
                                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto">
                                    {loading ? <><RefreshCw size={18} className="animate-spin" /> Starting…</> : "Start eKYC Verification"}
                                </button>
                            )}

                            {/* Step B — Polling */}
                            {ekycStep === "polling" && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-center gap-3 text-blue-600 font-medium">
                                        <RefreshCw size={20} className="animate-spin" />
                                        Waiting for customer to complete Aadhaar verification…
                                    </div>
                                    <p className="text-xs text-gray-400">This page checks automatically every 8 seconds.</p>
                                    {ekycUrl && (
                                        <a href={ekycUrl} target="_blank" rel="noreferrer"
                                            className="inline-block text-sm text-blue-600 underline">
                                            Re-open verification link
                                        </a>
                                    )}
                                </div>
                            )}

                            {/* Step D — Onboarding needed */}
                            {ekycStep === "onboarding" && (
                                <div className="space-y-4">
                                    <div className="p-4 bg-green-50 rounded-xl border border-green-200 text-green-700 text-sm max-w-sm mx-auto">
                                        <CheckCircle size={16} className="inline mr-2" />
                                        eKYC verified! Finalising customer onboarding…
                                    </div>
                                    <button onClick={handleEkycOnboard} disabled={loading}
                                        className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto">
                                        {loading ? <><RefreshCw size={18} className="animate-spin" /> Processing…</> : "Complete Onboarding"}
                                    </button>
                                </div>
                            )}

                            {/* Step C — Biometric (BIO KYC) */}
                            {(ekycStep === "bio_kyc" || ekycStep === "bio_capturing") && (
                                <div className="space-y-4 max-w-sm mx-auto">
                                    <DeviceStatus device={device} />
                                    <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto">
                                        <Fingerprint size={32} className="text-amber-600" />
                                    </div>
                                    <p className="text-gray-500 text-sm text-center">
                                        Place the customer's finger on the RD device scanner to capture and enroll biometric data.
                                    </p>
                                    {ekycStep === "bio_capturing" ? (
                                        <div className="flex items-center justify-center gap-3 text-amber-600 font-medium py-4">
                                            <RefreshCw size={20} className="animate-spin" />
                                            Capturing fingerprint…
                                        </div>
                                    ) : (
                                        <button onClick={handleBioEnroll} disabled={loading || rdLoading}
                                            className="w-full py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                            <Fingerprint size={18} />
                                            {loading || rdLoading ? "Processing…" : "Capture Fingerprint & Enroll"}
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Verified — proceed */}
                            {ekycStep === "verified" && (
                                <div className="space-y-4">
                                    <div className="p-4 bg-green-50 rounded-xl border border-green-200 text-green-700 text-sm max-w-sm mx-auto">
                                        <CheckCircle size={16} className="inline mr-2" />
                                        Customer onboarded successfully!
                                    </div>
                                    <button onClick={handleEkycDone} disabled={loading}
                                        className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto">
                                        {loading ? <><RefreshCw size={18} className="animate-spin" /> Please wait…</> : <>Proceed to Remittance <ChevronRight size={18} /></>}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══ SELECT RECEIVER ═══ */}
                    {screen === "select_receiver" && (
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center">
                                    <Users size={22} className="text-indigo-600" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Select Receiver</h2>
                                    <p className="text-gray-500 text-sm">{customer?.name} · {customer?.mobile}</p>
                                </div>
                            </div>

                            {(customer?.receivers || []).length > 0 ? (
                                <div className="space-y-3 mb-4">
                                    {customer.receivers.map((rec, i) => (
                                        <button key={i} onClick={() => handleSelectReceiver(rec)}
                                            className="w-full p-5 border-2 border-gray-200 hover:border-blue-500 rounded-xl flex items-center justify-between text-left transition-colors group">
                                            <div>
                                                <p className="font-semibold text-gray-900">{rec.name}</p>
                                                <p className="text-sm text-gray-500 mt-0.5">{rec.paymentMode} · {rec.acNumber}</p>
                                            </div>
                                            <ChevronRight size={20} className="text-gray-400 group-hover:text-blue-600" />
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-5 bg-gray-50 rounded-xl border border-gray-200 text-gray-500 text-sm mb-4 text-center">
                                    No receivers found for this customer.
                                </div>
                            )}

                            <button onClick={() => { setNewReceiver({ ...EMPTY_RECEIVER }); setBankSearch(""); setScreen("create_receiver"); }}
                                className="w-full py-4 border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2">
                                <Plus size={20} /> Add New Receiver
                            </button>
                        </div>
                    )}

                    {/* ═══ CREATE RECEIVER ═══ */}
                    {screen === "create_receiver" && (
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                                    <Plus size={22} className="text-green-600" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Add Receiver</h2>
                                    <p className="text-gray-500 text-sm">Receiver in Nepal for {customer?.name}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <SecHead title="Receiver Details" />
                                <div><Lbl text="First Name" req /><Inp value={newReceiver.firstName} onChange={(e) => setNewReceiver(p => ({...p, firstName: e.target.value}))} /></div>
                                <div><Lbl text="Middle Name" /><Inp value={newReceiver.middleName} onChange={(e) => setNewReceiver(p => ({...p, middleName: e.target.value}))} /></div>
                                <div><Lbl text="Last Name" req /><Inp value={newReceiver.lastName} onChange={(e) => setNewReceiver(p => ({...p, lastName: e.target.value}))} /></div>
                                <div>
                                    <Lbl text="Gender" req />
                                    <Sel value={newReceiver.gender} onChange={(e) => setNewReceiver(p => ({...p, gender: e.target.value}))}>
                                        <option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
                                    </Sel>
                                </div>
                                <div>
                                    <Lbl text="Nepal Mobile Number" req />
                                    <Inp value={newReceiver.mobile} onChange={(e) => setNewReceiver(p => ({...p, mobile: e.target.value.replace(/\D/g, "")}))} placeholder="Nepal mobile" />
                                    <p className="text-xs text-red-500 font-bold mt-1">Mobile number should start with 980, 981, or 982.</p>
                                </div>
                                <div>
                                    <Lbl text="Relationship" req />
                                    <Sel value={newReceiver.relationship} onChange={(e) => setNewReceiver(p => ({...p, relationship: e.target.value}))}>
                                        <option value="">Select</option>
                                        {refData.relationships.length > 0
                                            ? refData.relationships.map((r, i) => <option key={i} value={optLbl(r)}>{optLbl(r)}</option>)
                                            : <><option value="SPOUSE">Spouse</option><option value="PARENT">Parent</option><option value="CHILD">Child</option><option value="SIBLING">Sibling</option><option value="OTHER">Other</option></>
                                        }
                                    </Sel>
                                </div>

                                <SecHead title="Nepal Address" />
                                <div className="col-span-2"><Lbl text="Address" req /><Inp value={newReceiver.address} onChange={(e) => setNewReceiver(p => ({...p, address: e.target.value}))} placeholder="Full address in Nepal" /></div>
                                <div>
                                    <Lbl text="State / Province" req />
                                    <Sel value={newReceiver.state} onChange={(e) => setNewReceiver(p => ({...p, state: e.target.value, district: "", localLevel: ""}))}>
                                        <option value="">Select province</option>
                                        {[...new Map((nepalGeoData || []).map(d => [d.stateCode, d.state])).entries()]
                                            .sort((a, b) => a[1].localeCompare(b[1]))
                                            .map(([code, name], i) => (
                                                <option key={i} value={code}>{name}</option>
                                            ))
                                        }
                                    </Sel>
                                </div>
                                <div>
                                    <Lbl text="District" req />
                                    <Sel value={newReceiver.district} onChange={(e) => setNewReceiver(p => ({...p, district: e.target.value, localLevel: ""}))} disabled={!newReceiver.state}>
                                        <option value="">Select district</option>
                                        {(nepalGeoData || []).filter(d => d.stateCode === newReceiver.state).map((d, i) => (
                                            <option key={i} value={d.district}>{d.district}</option>
                                        ))}
                                    </Sel>
                                </div>
                                <div className="col-span-2">
                                    <Lbl text="Local Level / Municipality" req />
                                    <Sel value={newReceiver.localLevel} onChange={(e) => setNewReceiver(p => ({...p, localLevel: e.target.value}))} disabled={!newReceiver.district}>
                                        <option value="">Select local level</option>
                                        {(nepalGeoData || [])
                                            .filter(d => d.stateCode === newReceiver.state && d.district === newReceiver.district && d.localLevel)
                                            .map((d, i) => (
                                                <option key={i} value={d.localLevel}>{d.localLevel}</option>
                                            ))
                                        }
                                    </Sel>
                                </div>

                                <SecHead title="Payment Details" />
                                <div className="col-span-2">
                                    <Lbl text="Payment Mode" req />
                                    <Sel value={newReceiver.paymentMode} onChange={(e) => setNewReceiver(p => ({...p, paymentMode: e.target.value}))}>
                                        {refData.paymentModes.length > 0
                                            ? refData.paymentModes.map((m, i) => <option key={i} value={optVal(m)}>{optLbl(m)}</option>)
                                            : <><option value="Account Deposit">Bank Deposit</option><option value="Payment Mode">Cash Pickup</option></>
                                        }
                                    </Sel>
                                </div>

                                {newReceiver.paymentMode === "Account Deposit" && (
                                    <>
                                        <div className="col-span-2 relative">
                                            <Lbl text="Search Bank / Branch" req />
                                            <Inp
                                                value={bankSearch}
                                                onChange={(e) => setBankSearch(e.target.value)}
                                                onFocus={() => setShowBankDrop(true)}
                                                onBlur={() => setTimeout(() => setShowBankDrop(false), 200)}
                                                placeholder="Type bank name..."
                                            />
                                            {newReceiver.bankBranchId && (
                                                <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><CheckCircle size={12} /> Branch selected</p>
                                            )}
                                            {showBankDrop && banks.length > 0 && (
                                                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                                    {banks.map((b, i) => (
                                                        <button key={i} type="button"
                                                            className="w-full text-left px-4 py-3 hover:bg-blue-50 text-sm border-b border-gray-100 last:border-0"
                                                            onMouseDown={() => {
                                                                setNewReceiver(p => ({...p, bankBranchId: b.bankBranchId || b.branchId || b.id || ""}));
                                                                setBankSearch(`${b.bankName} — ${b.branchName}`);
                                                                setShowBankDrop(false);
                                                            }}
                                                        >
                                                            <p className="font-semibold text-gray-800">{b.bankName}</p>
                                                            <p className="text-xs text-gray-500">{b.branchName}</p>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-span-2"><Lbl text="Account Number" req /><Inp value={newReceiver.accountNumber} onChange={(e) => setNewReceiver(p => ({...p, accountNumber: e.target.value}))} placeholder="Bank account number" /></div>
                                    </>
                                )}

                            </div>

                            <button onClick={handleCreateReceiver} disabled={loading}
                                className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-semibold disabled:opacity-50 transition-colors">
                                {loading ? "Creating Receiver..." : "Add Receiver & Continue →"}
                            </button>
                        </div>
                    )}

                    {/* ═══ INITIATE ═══ */}
                    {screen === "initiate" && (
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                                    <CreditCard size={22} className="text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Initiate Transfer</h2>
                                    <p className="text-gray-500 text-sm">Receiver: {selectedReceiver?.name}</p>
                                </div>
                            </div>

                            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 mb-6 grid grid-cols-2 gap-y-2 text-sm">
                                <span className="text-gray-500">Customer</span><span className="font-semibold text-gray-800">{customer?.name}</span>
                                <span className="text-gray-500">Mobile</span><span className="font-semibold text-gray-800">{customer?.mobile}</span>
                                <span className="text-gray-500">Receiver</span><span className="font-semibold text-gray-800">{selectedReceiver?.name}</span>
                                <span className="text-gray-500">Payment Mode</span><span className="font-semibold text-gray-800">{selectedReceiver?.paymentMode}</span>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Transfer Amount (INR) <span className="text-red-500">*</span></label>
                                <div className="flex items-center border-2 border-gray-300 rounded-xl overflow-hidden focus-within:border-blue-500">
                                    <span className="px-4 py-4 bg-gray-50 text-gray-600 font-bold border-r border-gray-200">₹</span>
                                    <input
                                        type="number" min="0"
                                        value={txnForm.sendAmount}
                                        onChange={(e) => setTxnForm(p => ({...p, sendAmount: e.target.value}))}
                                        onKeyDown={(e) => e.key === "Enter" && handleInitiate()}
                                        placeholder="0.00"
                                        className="flex-1 px-4 py-4 text-lg font-semibold focus:outline-none bg-white"
                                    />
                                    <span className="px-4 text-gray-400 text-sm">INR</span>
                                </div>
                            </div>

                            {feeData && (
                                <div className="p-5 bg-blue-50 rounded-xl border border-blue-200 mb-6">
                                    <h3 className="font-bold text-blue-900 mb-4 text-sm uppercase tracking-wide">Fee Breakdown</h3>
                                    <div className="space-y-2">
                                        {[
                                            ["Transfer Amount",        `₹ ${Number(feeData.transferAmount)?.toLocaleString("en-IN")}`],
                                            ["Service Charge (Prabhu)", `₹ ${Number(feeData.serviceCharge)?.toLocaleString("en-IN")}`],
                                            ["Platform Fee",           `₹ ${Number(feeData.platformFee)?.toLocaleString("en-IN")}`],
                                            ["Exchange Rate",          `1 INR = ${feeData.exchangeRate} NPR`],
                                            ["Receiver Gets",          `NPR ${Number(feeData.payoutAmount)?.toLocaleString("en-IN")}`],
                                        ].map(([k, v]) => (
                                            <div key={k} className="flex justify-between text-sm">
                                                <span className="text-blue-700">{k}</span>
                                                <span className="font-semibold text-blue-900">{v}</span>
                                            </div>
                                        ))}
                                        <div className="pt-3 mt-2 border-t border-blue-200 flex justify-between">
                                            <span className="font-bold text-blue-900">Total to Collect</span>
                                            <span className="font-bold text-blue-900">₹ {Number(feeData.collectionAmount)?.toLocaleString("en-IN")}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button onClick={handleInitiate} disabled={loading}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                                <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
                                {loading ? "Calculating fees..." : feeData ? "Re-calculate & Get OTP →" : "Calculate Fees →"}
                            </button>
                        </div>
                    )}

                    {/* ═══ CONFIRM ═══ */}
                    {screen === "confirm" && (
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                                    <Send size={22} className="text-green-600" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Confirm & Send</h2>
                                    <p className="text-gray-500 text-sm">OTP has been sent to customer's mobile</p>
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50 rounded-xl border border-gray-200 mb-6">
                                <Row label="Customer"       value={customer?.name} />
                                <Row label="Receiver"       value={selectedReceiver?.name} />
                                <Row label="Send Amount"    value={`₹ ${Number(feeData?.transferAmount)?.toLocaleString("en-IN")}`} />
                                <Row label="Service Charge" value={`₹ ${Number(feeData?.serviceCharge)?.toLocaleString("en-IN")}`} />
                                <Row label="Platform Fee"   value={`₹ ${Number(feeData?.platformFee)?.toLocaleString("en-IN")}`} />
                                <div className="flex justify-between pt-2 mt-1 border-t border-gray-200">
                                    <span className="font-bold text-gray-900">Total to Collect</span>
                                    <span className="font-bold text-blue-700 text-lg">₹ {Number(feeData?.collectionAmount)?.toLocaleString("en-IN")}</span>
                                </div>
                                <div className="flex justify-between mt-2 text-sm">
                                    <span className="text-gray-500">Receiver Gets</span>
                                    <span className="font-bold text-gray-800">NPR {Number(feeData?.payoutAmount)?.toLocaleString("en-IN")}</span>
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Customer OTP (sent to {customer?.mobile}) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text" maxLength={6} value={txnOtp}
                                    onChange={(e) => setTxnOtp(e.target.value.replace(/\D/g, ""))}
                                    placeholder="• • • • • •"
                                    className="w-full border-2 border-gray-300 rounded-xl px-4 py-4 text-3xl tracking-[0.5em] text-center font-mono focus:outline-none focus:border-blue-500"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div>
                                    <Lbl text="Remittance Reason" req />
                                    <Sel value={txnForm.remittanceReason} onChange={(e) => setTxnForm(p => ({...p, remittanceReason: e.target.value}))}>
                                        <option value="">Select reason</option>
                                        {refData.remittanceReasons.length > 0
                                            ? refData.remittanceReasons.map((r, i) => <option key={i} value={optVal(r)}>{optLbl(r)}</option>)
                                            : <><option value="FAMILY_MAINTENANCE">Family Maintenance</option><option value="EDUCATION">Education</option><option value="MEDICAL">Medical</option><option value="BUSINESS">Business</option><option value="OTHER">Other</option></>
                                        }
                                    </Sel>
                                </div>
                                <div>
                                    <Lbl text="Source of Fund" req />
                                    <Sel value={txnForm.sourceOfFund} onChange={(e) => setTxnForm(p => ({...p, sourceOfFund: e.target.value}))}>
                                        <option value="">Select source</option>
                                        {refData.sourcesOfFund.length > 0
                                            ? refData.sourcesOfFund.map((r, i) => <option key={i} value={optVal(r)}>{optLbl(r)}</option>)
                                            : <><option value="EMPLOYMENT">Employment</option><option value="BUSINESS">Business</option><option value="SAVINGS">Savings</option><option value="OTHER">Other</option></>
                                        }
                                    </Sel>
                                </div>
                            </div>

                            <button onClick={handleSend} disabled={loading || txnOtp.length < 4}
                                className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold disabled:opacity-50 transition-colors flex items-center justify-center gap-2 text-lg">
                                <Send size={22} />
                                {loading ? "Processing..." : "Send Money to Nepal"}
                            </button>
                        </div>
                    )}

                    {/* ═══ RESULT ═══ */}
                    {screen === "result" && txnResult && (
                        <div className="text-center py-6">
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                                txnResult.txnStatus === "UN_PAID" || txnResult.txnStatus === "PAID"
                                    ? "bg-green-50" : "bg-amber-50"
                            }`}>
                                {txnResult.txnStatus === "UN_PAID" || txnResult.txnStatus === "PAID"
                                    ? <CheckCircle size={44} className="text-green-600" />
                                    : <Clock size={44} className="text-amber-500" />
                                }
                            </div>

                            <h2 className={`text-2xl font-bold mb-2 ${
                                txnResult.txnStatus === "UN_PAID" || txnResult.txnStatus === "PAID"
                                    ? "text-green-700" : "text-amber-700"
                            }`}>
                                {txnResult.txnStatus === "UN_PAID"  && "Transaction Successful!"}
                                {txnResult.txnStatus === "PAID"     && "Transaction Paid!"}
                                {txnResult.txnStatus === "PENDING"  && "Confirmation Pending"}
                                {txnResult.txnStatus === "FAILED"   && "Transaction Failed"}
                            </h2>

                            <p className="text-gray-500 text-sm mb-8">
                                {txnResult.txnStatus === "UN_PAID" && "Money confirmed and in transit to Nepal."}
                                {txnResult.txnStatus === "PENDING" && "Auto-confirm is pending — use the PIN below to verify status."}
                            </p>

                            <div className="p-6 bg-gray-50 rounded-2xl border text-left max-w-md mx-auto mb-6">
                                {txnResult.prabhuPinNo  && <Row label="Prabhu PIN"    value={txnResult.prabhuPinNo} />}
                                {txnResult.partnerPinNo && <Row label="Partner PIN"   value={txnResult.partnerPinNo} />}
                                <Row label="Status"         value={txnResult.txnStatus} />
                                <Row label="Send Amount"    value={`₹ ${Number(txnResult.sendAmount)?.toLocaleString("en-IN")}`} />
                                <Row label="Service Charge" value={`₹ ${Number(txnResult.serviceCharge)?.toLocaleString("en-IN")}`} />
                                <Row label="Platform Fee"   value={`₹ ${Number(txnResult.platformFee)?.toLocaleString("en-IN")}`} />
                                <Row label="Total Collected" value={`₹ ${Number(txnResult.collectedAmount)?.toLocaleString("en-IN")}`} />
                                <Row label="Payout Amount"  value={`NPR ${Number(txnResult.payAmount)?.toLocaleString("en-IN")}`} />
                                {txnResult.exchangeRate && <Row label="Exchange Rate" value={`${txnResult.exchangeRate}`} />}
                            </div>

                            {txnResult.txnStatus === "PENDING" && (
                                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-700 text-sm max-w-md mx-auto mb-6">
                                    Auto-confirm failed. Use Partner PIN <strong>{txnResult.partnerPinNo}</strong> to verify status with support.
                                </div>
                            )}

                            <button onClick={handleReset}
                                className="px-10 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors">
                                New Transaction
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </div>

        {/* ═══ DOCUMENT UPLOAD MODAL ═══ */}
        {showUploadModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">

                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center flex-shrink-0">
                            <Upload size={22} className="text-orange-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Upload KYC Document</h2>
                            <p className="text-gray-500 text-sm">
                                Required for {newCustomer.idType} — upload to complete registration
                            </p>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
                            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}

                    <div className="space-y-4">
                        {[
                            ["frontImg",      "Front Page",           true ],
                            ["backImg",       "Back Page",            false],
                            ["additionalImg", "Selfie with Document", false],
                        ].map(([field, label, required]) => (
                            <div key={field} className="border-2 border-dashed border-gray-300 rounded-xl p-4">
                                <p className="text-sm font-semibold text-gray-700 mb-2">
                                    {label}{required && <span className="text-red-500 ml-1">*</span>}
                                </p>
                                <input
                                    type="file" accept="image/*"
                                    onChange={(e) => handleFileChange(field, e.target.files?.[0])}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                                {docFiles[field] && (
                                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                                        <CheckCircle size={12} /> Image loaded
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={handleUploadAndProceed}
                        disabled={loading || !docFiles.frontImg}
                        className="w-full mt-6 bg-orange-600 hover:bg-orange-700 text-white py-4 rounded-xl font-bold text-base disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
                    >
                        {loading ? (
                            <><RefreshCw size={20} className="animate-spin" /> Uploading…</>
                        ) : (
                            <><Upload size={20} /> Upload &amp; Continue</>
                        )}
                    </button>

                    <p className="text-center text-xs text-gray-400 mt-3">
                        Document upload is required before proceeding.
                    </p>
                </div>
            </div>
        )}
        {/* Customer created success popup */}
        {showCreateSuccess && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mx-auto mb-4">
                        <CheckCircle size={36} className="text-green-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Customer Created</h2>
                    <p className="text-gray-600 text-sm mb-6">
                        Customer created successfully. Please search again using the same mobile number.
                    </p>
                    <button
                        onClick={handleCreateSuccessOk}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold text-base transition-colors"
                    >
                        OK
                    </button>
                </div>
            </div>
        )}
        </>
    );
}
